import React, { useState } from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import SettingsIcon from "@mui/icons-material/Settings";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import WizardContainer from "./WizardContainer";
import { WizardProvider, useWizard } from "./WizardContext";
import WizardIntroduction from "./steps/WizardIntroduction";
import BlockSelector from "./steps/BlockSelector";
import StyleCustomizer from "./steps/StyleCustomizer";
import ReviewExport from "./steps/ReviewExport";

/**
 * Main Wizard Application Component
 * Handles routing between wizard mode and advanced mode
 */
const WizardApp = ({ theme, existingApp, blockConfigs }) => {
  const [mode, setMode] = useState("wizard"); // "wizard" or "advanced"

  // If in advanced mode, render the existing app
  if (mode === "advanced") {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setMode("wizard")}
            sx={{ 
              bgcolor: "#CE950F",
              "&:hover": { bgcolor: "#B5850D" }
            }}
          >
            Switch to Wizard
          </Button>
        </Box>
        {existingApp}
      </ThemeProvider>
    );
  }

  // Render wizard mode
  return (
    <ThemeProvider theme={theme}>
      <WizardProvider initialBlockConfigs={blockConfigs}>
        <WizardMain onSwitchToAdvanced={() => setMode("advanced")} />
      </WizardProvider>
    </ThemeProvider>
  );
};

/**
 * Internal component that uses the wizard context
 */
const WizardMain = ({ onSwitchToAdvanced }) => {
  const { currentStep, setCurrentStep, isStepValid } = useWizard();

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WizardIntroduction />;
      case 1:
        return <BlockSelector />;
      case 2:
        return <StyleCustomizer />;
      case 3:
        return <ReviewExport />;
      default:
        return <WizardIntroduction />;
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Advanced Mode Toggle - Hidden for now */}
      {false && (
        <Box sx={{ position: "fixed", top: 16, right: 16, zIndex: 1000 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={onSwitchToAdvanced}
            sx={{ 
              bgcolor: "white",
              "&:hover": { bgcolor: "#f5f5f5" }
            }}
          >
            Advanced Mode
          </Button>
        </Box>
      )}

      {/* Wizard Container */}
      <WizardContainer
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        totalSteps={4}
        stepTitles={["Introduction", "Select Blocks", "Customize Style", "Review & Export"]}
        isStepValid={isStepValid}
        showProgress={currentStep > 0}
      >
        {renderStepContent()}
      </WizardContainer>
    </Box>
  );
};


export default WizardApp;